<script lang="ts">
	import classNames from 'classnames';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { getBookDisplayNames } from '#lib/features/publishing/utils/getBookDisplayNames/index.js';
	import { getBookPrices } from '#lib/features/publishing/utils/getBookPrices/getBookPrices.js';
	import { BookLabel } from '#lib/websites/publishing/components/BookLabel/index.js';
	import { PublishingRoutes } from '#lib/websites/publishing/constants/routes.js';
	import { getBookLabelText } from '#lib/websites/publishing/utils/getBookLabel/getBookLabel.js';
	import type { ShopPdpRelatedBook } from '#lib/websites/shop/pages/ShopBookPdp/types.js';
	import { page } from '$app/state';
	import { ArtworkCard } from '$global/components/ArtworkCard';
	import { Container } from '$global/components/Container';
	import { GlobalCard } from '$global/components/GlobalCard';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { ShopBooksPDPPageData } from '$routes/shop/books/[slug]/types';

	type RelatedBook = ShopPdpRelatedBook | null | undefined;

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();

	let rawRelatedBooks = $derived(
		page.data.relatedBooks as ShopBooksPDPPageData['relatedBooks']
	);

	let relatedBooks = $derived(
		(rawRelatedBooks || [])
			.map((item) => item?.related_Publishing_Books_id)
			.filter(Boolean)
	);

	let bookPrices = $derived(
		(relatedBooks || []).map((book: RelatedBook) => {
			const bookSeriesId = book?.series?.id;
			const bookPrices = getBookPrices(
				book as unknown as Parameters<typeof getBookPrices>[0]
			);

			if (bookSeriesId) {
				const bookSeriesHref = `${PublishingRoutes.Series}/${bookSeriesId}`;
				return page.url.pathname === bookSeriesHref
					? getBookPrices({ ...book, series: null } as unknown as Parameters<
							typeof getBookPrices
						>[0])
					: bookPrices;
			}

			return bookPrices;
		})
	);

	let bookHrefs = $derived(
		(relatedBooks || []).map((book: RelatedBook) => {
			const bookSeriesId = book?.series?.id;
			const bookHref = `${GlobalRoutes.PublishingHome}/${book?.slug || ''}`;
			if (bookSeriesId) {
				const bookSeriesHref = `${PublishingRoutes.Series}/${bookSeriesId}`;
				return page.url.pathname === bookSeriesHref ? bookHref : bookSeriesHref;
			}

			return bookHref;
		})
	);
</script>

<Container {dataCy}>
	{#if relatedBooks?.length && relatedBooks?.length > 0}
		<div class="mb-10 border-b border-gray-200 sm:mb-12 lg:mb-10"></div>
		<Txt variant="h4" class="mb-6">Books related to this item</Txt>
		<div
			class={classNames({
				'mb-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:mb-16 lg:grid-cols-4':
					page.url.pathname.startsWith(GlobalRoutes.ShopHome),
				'xs:flex-rows xs:grid xs:grid-cols-2 mb-16 flex w-full flex-col gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5':
					page.url.pathname.startsWith(GlobalRoutes.PublishingHome),
			})}
		>
			{#each relatedBooks as book, i}
				{#if book}
					{@const labelText = getBookLabelText(book)}
					{@const { price, previousPrice } = bookPrices[i]}
					{@const featuredImage = {
						url: getImageUrl(book?.featured_image?.filename_disk),
						width: book?.featured_image?.width || 0,
						height: book?.featured_image?.height || 0,
					}}
					{@const { names: displayName } = getBookDisplayNames(book)}

					<div class="relative">
						{#if labelText}
							<BookLabel
								class="xs:block absolute top-[8px] left-[8px] z-10 hidden py-1"
							>
								{labelText}
							</BookLabel>
						{/if}

						{#if page.url.pathname.startsWith(GlobalRoutes.PublishingHome)}
							<GlobalCard
								{dataCy}
								image={featuredImage}
								line1={book?.title || ''}
								line2={displayName}
								line3={`${price}`}
								href={bookHrefs[i]}
								alt={book?.title || ''}
								class="xs:hidden"
							>
								{#if labelText}
									<BookLabel class="mb-2 py-1">
										{labelText}
									</BookLabel>
								{/if}

								{#snippet line3Addition()}
									<span class="ml-1 text-gray-500 line-through empty:hidden">
										{#if previousPrice}
											{previousPrice}
										{/if}
									</span>
								{/snippet}
							</GlobalCard>
						{/if}

						<ArtworkCard
							{dataCy}
							href={bookHrefs[i]}
							line1={book?.title || ''}
							line2={displayName}
							image={featuredImage}
							alt={book?.title || ''}
							class={classNames({
								'xs:block hidden': page.url.pathname.startsWith(
									GlobalRoutes.PublishingHome
								),
							})}
						>
							<Txt
								variant="label3"
								class="mt-1 text-gray-700"
								dataCy={`${dataCy}-artwork-card-line-3`}
							>
								{price}

								{#if previousPrice}
									<span class="ml-1 text-gray-500 line-through"
										>{previousPrice}</span
									>
								{/if}
							</Txt>
						</ArtworkCard>
					</div>
				{/if}
			{/each}
		</div>
	{/if}
</Container>
